import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, type Inquiry } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import InquiryForm, { type InquiryCategory } from './components/InquiryForm';
import InquiryList from './components/InquiryList';

// 비로그인 문의를 저장할 때 사용하는 익명 식별자 (user_id 컬럼이 NOT NULL 이라 필요)
const ANONYMOUS_USER_ID = '00000000-0000-0000-0000-000000000000';

export default function InquiryPage() {
  const navigate = useNavigate();
  const { profile, loading: authLoading } = useAuth();
  const [inquiries, setInquiries] = useState<Inquiry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [submitError, setSubmitError] = useState('');
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const isLoggedIn = Boolean(profile);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data, error: err } = await supabase
        .from('inquiries')
        .select('*')
        .order('created_at', { ascending: false });
      if (err) throw err;
      setInquiries((data as Inquiry[]) || []);
    } catch (e) {
      setError('문의 내역을 불러오지 못했습니다.');
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isLoggedIn) {
      load();
    } else {
      setInquiries([]);
      setLoading(false);
    }
  }, [isLoggedIn, load]);

  const handleSubmit = async (data: {
    category: InquiryCategory;
    title: string;
    content: string;
    email?: string;
  }): Promise<boolean> => {
    setSubmitError('');
    setSubmitSuccess(false);
    try {
      if (isLoggedIn) {
        const { data: session } = await supabase.auth.getSession();
        const userId = session.session?.user.id;
        if (!userId) {
          setSubmitError('로그인 정보를 확인할 수 없습니다. 다시 로그인해주세요.');
          return false;
        }
        const { error: err } = await supabase.from('inquiries').insert({
          user_id: userId,
          category: data.category,
          title: data.title,
          content: data.content,
        });
        if (err) throw err;
      } else {
        // 비로그인: 익명 식별자와 입력받은 이메일로 접수
        const { error: err } = await supabase.from('inquiries').insert({
          user_id: ANONYMOUS_USER_ID,
          email: data.email?.trim() || null,
          category: data.category,
          title: data.title,
          content: data.content,
        });
        if (err) throw err;
      }

      setSubmitSuccess(true);
      if (isLoggedIn) await load();
      return true;
    } catch (e) {
      setSubmitError('문의 접수 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
      console.error(e);
      return false;
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <header className="bg-white border-b border-background-200">
        <div className="max-w-3xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-100 text-primary-700">
              <i className="ri-chat-3-line text-lg"></i>
            </div>
            <div>
              <h1 className="text-sm font-semibold text-foreground-950 leading-tight">
                문의 및 건의사항
              </h1>
              <p className="text-xs text-foreground-500">궁금한 점을 남겨주세요</p>
            </div>
          </div>
          <button
            onClick={() => navigate(isLoggedIn ? '/dashboard' : '/')}
            className="whitespace-nowrap inline-flex items-center gap-2 px-4 py-2 rounded-md border border-background-300 text-foreground-700 hover:bg-background-100 text-sm transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line"></i>
            {isLoggedIn ? '대시보드' : '홈으로'}
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 md:px-6 py-8 space-y-6">
        {error && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3 flex items-center justify-between gap-3">
            <span>{error}</span>
            <button
              onClick={load}
              className="whitespace-nowrap text-red-600 font-semibold hover:underline cursor-pointer text-xs"
            >
              다시 시도
            </button>
          </div>
        )}

        {submitError && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
            {submitError}
          </div>
        )}

        {submitSuccess && (
          <div className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-4 py-3 flex items-start gap-2">
            <i className="ri-checkbox-circle-line text-emerald-600 mt-0.5"></i>
            <span>
              문의가 정상적으로 접수되었습니다. 운영자가 확인 후 답변해드릴게요.
              {!isLoggedIn && ' 답변은 입력하신 이메일로 안내드립니다.'}
            </span>
          </div>
        )}

        {!authLoading && (
          <InquiryForm onSubmit={handleSubmit} requireEmail={!isLoggedIn} />
        )}
        {isLoggedIn && <InquiryList inquiries={inquiries} loading={loading} />}
      </main>
    </div>
  );
}